/* eslint-disable no-underscore-dangle, no-multi-spaces */
import axios from 'axios';
import { clearOldSession, loginUserViaSessionData } from './_shared';

const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT;
const IDP_SESSION_CALL_TIMEOUT = 20000; // 20 seconds
const currentRoot = window.location.origin;

// TODO: tmp override for localhost
// if (currentRoot.indexOf('//localhost') !== -1) {
//   currentRoot = 'https://election-ui-dev-mwong.micruity-svc.com';
// }
const urlParams = new URLSearchParams(window.location.search);

const API_ROUTES = {
  identityProviderSessions : idpName => `${API_BASE_URL}/auth/sso/idps/${idpName}/sessions`,
  identityProvider         : idpName => `${API_BASE_URL}/auth/sso/idps/${idpName}`,
};
const FRONT_END_ROUTES = {
  ssoLoginUrl: idpName => `${currentRoot}/${idpName}/auth/sso/login`,
  // electionUrl: (rkCode, electionGuid) => `/${rkCode}/election/${electionGuid}`,
  afterLoginRedirectUrl: (shuttleRoot, electionGuid) => { // TODO: put this url on the shuttle record
    if (electionGuid) { // election shuttle (i.e. fidelity GID)
      return `/${shuttleRoot}/election/${electionGuid}`;
    }
    // no election shuttle (i.e. ssga IncomeWise)
    return `/${shuttleRoot}`;
  },
};
const config = {};

let __abortController;

const cancelCurrentRequest = function cancelCurrentRequestFn() {
  if (__abortController) {
    __abortController.abort();
  }
};

const init = async function initFn(idpName) {
  const idpConfig = await __loadIdpConfig(idpName);

  Object.assign(config, {
    ...idpConfig,
    name: idpName,
  });
  return Boolean(idpConfig);
};

const buildAuthorizeUrlForIdp = function buildAuthorizeUrlForIdpFn(customUrlParams = { pfidpadapterid : 'DARefAdapterForNB' }) {
  const stateParam = urlParams.get('state');

  const defaultParams = {
    client_id      : config.client_id,
    scope          : config.authorize_scopes,
    response_type  : 'code',
    redirect_uri   : FRONT_END_ROUTES.ssoLoginUrl(config.name),
    state          : stateParam,
  };
  const authorizeUrlParams = { ...defaultParams, ...customUrlParams };
  const fidelityLoginUrl = moduleExports.__buildUrlWithParams(config.authorize_url, authorizeUrlParams);

  return fidelityLoginUrl;
};

const tryLoginWithIdpCode = async function tryLoginWithIdpCodeFn() {
  const codeParam = urlParams.get('code');
  const stateParam = urlParams.get('state');
  if (!codeParam || !stateParam) {
    throw new Error('Both code and state parameters are required!');
  }
  /*
    Example : https://micruity.com/Fidelity/auth/sso/redirect?state=12345-00001234
    Format  : https://micruity.com/Fidelity/auth/sso/redirect?state={{IDBPlanNumber}}-{{IDBClientID}}
    Where :
    IDB DC plan number = 5 digit (If plan number is not 5 digit 0s will be added in the front)
    IDB Client ID = 10 digit (If client ID is not 10 digit 0s will be added in the front)
    Seperator = "-" used as seperator
    This syntax is subject to Fidelity ISO approval.
   */
  // eslint-disable-next-line no-unused-vars
  const [idpPlanId, idpClientId] = stateParam.split('-');

  const { session, username, meta } = await __createSessionFromParameters({ code: codeParam, idpPlanId, idpClientId });
  // const redirectToPath = FRONT_END_ROUTES.electionUrl(meta.rk_code, meta.election_guid);
  const redirectToPath = FRONT_END_ROUTES.afterLoginRedirectUrl(meta.shuttle_root, meta.election_guid);
  return { session, username, redirectToPath };
};

const __createSessionFromParameters = async function __createSessionFromParametersFn(parameters = {}) {
  await clearOldSession();

  const { data: loggedInData } = await axios.post(API_ROUTES.identityProviderSessions(config.name), {
    ...parameters,
    redirectUrl: FRONT_END_ROUTES.ssoLoginUrl(config.name),
  }, { timeout: IDP_SESSION_CALL_TIMEOUT });

  await loginUserViaSessionData(loggedInData);

  return loggedInData;
};

const __loadIdpConfig = async function __loadIdpConfigFn(idpName) {
  __abortController = new AbortController();
  try {
    const { data } = await axios.get(API_ROUTES.identityProvider(idpName), {
      signal: __abortController.signal,
    });
    return data;
  } catch (error) {
    if (axios.isCancel(error)) {
      return null;
    }
    throw error;
  }
};

const __buildUrlWithParams = function buildUrlWithParamsFn(urlString, params = {}) {
  const hashIndex = urlString.indexOf('#');
  const queryIndex = urlString.indexOf('?');

  // Custom Fidelity format: variables passed via fragment, example: `.../authorize#/login/?client_id=...`
  const isFragmentParamFormat = hashIndex !== -1 && queryIndex > hashIndex + 1;
  if (isFragmentParamFormat) {
    const [base, fragment]   = urlString.split('#');
    const fragmentQueryIndex = fragment.indexOf('?');
    const fragmentPath       = fragment.slice(0, fragmentQueryIndex);
    const fragmentParams     = new URLSearchParams(fragment.slice(fragmentQueryIndex + 1));

    Object.entries(params).forEach(([key, value]) => {
      if (fragmentParams.get(key) === null) {
        fragmentParams.set(key, value);
      }
    });

    return `${base}#${fragmentPath}?${fragmentParams.toString()}`;
  }

  // Regular format: variables passed via query params, example: `.../authorize/login/?client_id=...`
  const url = new URL(urlString);
  Object.entries(params).forEach(([key, value]) => {
    if (url.searchParams.get(key) === null) {
      url.searchParams.set(key, value);
    }
  });
  return url.href;
};

const moduleExports = {
  init,
  buildAuthorizeUrlForIdp,
  tryLoginWithIdpCode,
  cancelCurrentRequest,
  __buildUrlWithParams,
  __createSessionFromParameters,
  __loadIdpConfig,
};

export default moduleExports;

/*
const buildAuthorizeUrlForIdpCOGNITO = async function buildAuthorizeUrlForIdpFn(idpName) {
  const _generateState = function (length) {
    var result = '';
    var i = length;
    var chars = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
    for (; i > 0; --i)
      result += chars[Math.round(Math.random() * (chars.length - 1))];
    return result;
  };

  const urlSafeEncode = function(str) {
    return str
      .split('')
      .map(char =>
        char
        .charCodeAt(0)
        .toString(16)
        .padStart(2, '0')
      )
      .join('');
  };

  const generatedState = _generateState(32);
  const customState    = urlParams.get('state');

  const state = generatedState + "-" + urlSafeEncode(customState);
  console.log(generatedState, state);

  const fidelityLoginUrl = moduleExports.__buildUrlWithParams(idpConfig.authorize_url, {
    client_id      : idpConfig.client_id,
    scope          : idpConfig.authorize_scopes,
    response_type  : 'code',
    redirect_uri   : 'https://eapp-dev-psirous.auth.us-east-1.amazoncognito.com/oauth2/idpresponse',
    state          : state,
    // pfidpadapterid : 'FidelityHeaderAdapterOpenIDNB',
  });

  return fidelityLoginUrl;
};
*/
